import React, { useState, useEffect, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import EmailInsights from "Components/common/EmailInsights";
import EmailComposeEditor from "Components/PreviewPopup/EmailComposeEditor";
import { emailPreviewContentPopupState } from "Components/PreviewPopup/EmailPreviewContentPopup";
import { ReactComponent as ReplySvg } from "Assets/svg/emailPreview/reply.svg";
import { ReactComponent as ReplyAllSvg } from "Assets/svg/emailPreview/replyAll.svg";
import { ReactComponent as ForwardSvg } from "Assets/svg/emailPreview/forward.svg";
import inboxV2Store from "./inboxV2Store";
import { formatDay, formatTime } from "./inboxV2Utils";
import { openInlineReply, closeInlineReply } from "./inlineReplyEditor";
import Avatar, { getInitials } from "./Avatar";

const ACTIONS = [
  { mode: "reply", label: "Reply", Icon: ReplySvg },
  { mode: "replyAll", label: "Reply All", Icon: ReplyAllSvg },
  { mode: "forward", label: "Forward", Icon: ForwardSvg }
];

const AddressRow = ({ label = "", children = null }) => (
  <div className="inboxV2AddressRow">
    <span className="inboxV2AddressLabel">{label}</span>
    <div className="inboxV2AddressValue">{children}</div>
  </div>
);

// Split the quoted reply thread (Gmail's .gmail_quote / <blockquote>) off the body so it
// can be collapsed behind a toggle, like Gmail's trimmed-content "•••".
const splitQuoted = (html = "") => {
  if (!html || typeof document === "undefined") return { main: html, quoted: "" };
  const div = document.createElement("div");
  div.innerHTML = html;
  const marker = div.querySelector(".gmail_quote, blockquote");
  if (!marker) return { main: html, quoted: "" };
  const quoted = document.createElement("div");
  let node = marker;
  const nodes = [];
  while (node) {
    nodes.push(node);
    node = node.nextSibling;
  }
  nodes.forEach(n => quoted.appendChild(n));
  return { main: div.innerHTML.trim(), quoted: quoted.innerHTML };
};

const MessageBlock = ({ message = {}, onReply = () => {}, disabled = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { dir = "out", from = "", to = [], cc = [], body = "" } = message || {};
  const isOut = dir === "out";
  const { main, quoted } = useMemo(() => splitQuoted(body), [body]);

  return (
    <div className="inboxV2Message">
      <div className="inboxV2MessageHead">
        <Avatar
          initials={isOut ? "ME" : getInitials("", "", from)}
          filled={isOut}
          size="sm"
        />
        <div className="inboxV2MessageWho">
          <div className="inboxV2MessageFrom">{isOut ? "You" : from}</div>
          <div className="inboxV2MessageTo">
            {to?.length ? `To: ${to.join(", ")}` : ""}
            {cc?.length ? ` · Cc: ${cc.join(", ")}` : ""}
          </div>
        </div>
        <span className="inboxV2MessageDate">
          {formatDay(message?.date)}
          {message?.date ? ` · ${formatTime(message.date)}` : ""}
        </span>
        {/* Per-message actions: reply icon + a "more" menu with Reply All / Forward. */}
        <div className="inboxV2MsgActions">
          <button
            type="button"
            className="inboxV2IconBtn"
            title="Reply"
            disabled={disabled}
            onClick={() => onReply("reply")}
          >
            <ReplySvg />
          </button>
          <div className="inboxV2MoreWrap">
            <button
              type="button"
              className="inboxV2IconBtn"
              title="More"
              disabled={disabled}
              onClick={() => setMenuOpen(open => !open)}
            >
              <i className="material-icons">more_vert</i>
            </button>
            {menuOpen && (
              <>
                <div
                  className="inboxV2MenuOverlay"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="inboxV2Menu">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onReply("replyAll");
                    }}
                  >
                    <ReplyAllSvg />
                    Reply All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onReply("forward");
                    }}
                  >
                    <ForwardSvg />
                    Forward
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="inboxV2MessageContent">
        {message?.external && (
          <span className="inboxV2GmailChip inboxV2GmailTag">Sent via Gmail</span>
        )}
        <div
          className="inboxV2MessageBody"
          dangerouslySetInnerHTML={{ __html: main }}
        />
        {quoted && (
          <div className="inboxV2Quote">
            <button
              type="button"
              className="inboxV2QuoteToggle"
              title={quoteOpen ? "Hide quoted text" : "Show quoted text"}
              onClick={() => setQuoteOpen(open => !open)}
            >
              •••
            </button>
            {quoteOpen && (
              <div
                className="inboxV2QuoteBody"
                dangerouslySetInnerHTML={{ __html: quoted }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EmailThread = observer((props = {}) => {
  const { conversation = {} } = props || {};
  const [replyOpen, setReplyOpen] = useState(false);
  // The mode currently being opened (drives the button's "Opening..." state); "" once
  // the editor is up or the open failed.
  const [openingMode, setOpeningMode] = useState("");
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const wasOpenRef = useRef(false);
  const scrollRef = useRef(null);
  const { messages = [], stats = {} } = conversation || {};
  // The design's From/To/Cc header describes the newest message in the thread.
  const headerMessage = messages[messages.length - 1] || {};
  const hasCc = (headerMessage?.cc || []).length > 0;
  const outbound = messages.find(item => item?.dir === "out");
  const lastInbound = [...messages].reverse().find(item => item?.dir === "in");

  // The shared editor lives in global state; render it only while our reply is open.
  // Read the observables here so the observer re-renders and the effect below sees them.
  const editorComposeOpen = emailPreviewContentPopupState.showEditorComposePopup;
  const editorLoading = emailPreviewContentPopupState.editorLoading;
  const editorReady = editorComposeOpen && !emailPreviewContentPopupState.showPopup;

  // Reply threads off our original outbound task so the reply goes from the same inbox
  // we first sent from (headerMessage may be the contact's inbound reply / a Gmail task).
  const startReply = async mode => {
    if (openingMode) return; // ignore rapid re-clicks while a fetch is in flight
    setReplyOpen(true);
    setOpeningMode(mode);
    const ok = await openInlineReply(outbound?.taskId || headerMessage?.taskId, {
      mode,
      emailData: {
        subject: conversation?.subject || "",
        from: { email: outbound?.from || "" }
      }
    });
    setOpeningMode("");
    if (!ok && !emailPreviewContentPopupState.showEditorComposePopup) {
      setReplyOpen(false);
    }
  };

  // The editor closes itself (setResetPopup) on send or cancel; mirror that here and
  // refresh (silently -- only the thread, not the whole pane) so a just-sent reply shows
  // up. Keyed off the editor actually having been open (wasOpenRef) -- not off replyOpen
  // -- so the async open (fetch then flip) isn't torn down before the editor mounts.
  useEffect(() => {
    if (wasOpenRef.current && !editorComposeOpen) {
      setReplyOpen(false);
      const contactId = inboxV2Store.getContactId(
        inboxV2Store?.selectedContact || {}
      );
      if (contactId) inboxV2Store.getConversations(contactId, { silent: true });
    }
    wasOpenRef.current = editorComposeOpen;
  }, [editorComposeOpen]);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
    setShowScrollBtn(!atBottom);
  };

  // Leaving the thread (switch conversation / unmount) must not leave editor state open.
  useEffect(() => {
    return () => {
      if (
        emailPreviewContentPopupState.showEditorComposePopup &&
        !emailPreviewContentPopupState.showPopup
      ) {
        closeInlineReply();
      }
    };
  }, []);

  return (
    <div className="inboxV2ThreadPane">
      <div className="inboxV2ThreadScroll" ref={scrollRef} onScroll={onScroll}>
        <div className="inboxV2ThreadCard">
          <div className="inboxV2ThreadHead">
            <div className="inboxV2ThreadStats">
              <EmailInsights
                emailInsight={{
                  sentCount: stats?.sent || 0,
                  openCount: stats?.opens || 0,
                  clickCount: stats?.clicks || 0,
                  replyCount: stats?.replies || 0
                }}
              />
              <button
                type="button"
                className="inboxV2Close"
                onClick={() => inboxV2Store.clearSelection()}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="inboxV2ThreadSubject">
              <span className="inboxV2ThreadSubjectLabel">Subject:</span>
              <span className="inboxV2ThreadSubjectText">
                {conversation?.subject || "(no subject)"}
              </span>
            </div>
          </div>

          <div className="inboxV2ThreadAddresses">
            <AddressRow label="From:">
              <span className="inboxV2FromAddress">{headerMessage?.from}</span>
              <span className="inboxV2AddressDate">
                {formatDay(headerMessage?.date)}
                {headerMessage?.date
                  ? ` at ${formatTime(headerMessage.date)}`
                  : ""}
              </span>
            </AddressRow>
            <AddressRow label="To:">
              {(headerMessage?.to || []).map(address => (
                <span className="inboxV2AddressChip" key={address}>
                  {address}
                </span>
              ))}
            </AddressRow>
            {hasCc && (
              <AddressRow label="Cc:">
                {headerMessage.cc.map(address => (
                  <span className="inboxV2AddressChip" key={address}>
                    {address}
                  </span>
                ))}
              </AddressRow>
            )}
          </div>

          <div className="inboxV2ThreadMessages">
            {messages.map(message => (
              <MessageBlock
                key={message?.id}
                message={message}
                onReply={mode => startReply(mode)}
                disabled={!!openingMode}
              />
            ))}
          </div>

          {/* Bottom action row (in addition to the per-message icons above). */}
          <div className="inboxV2ThreadActions">
            {ACTIONS.map(({ mode, label, Icon }) => (
              <button
                key={mode}
                type="button"
                disabled={!!openingMode}
                className={`${mode === "reply" ? "inboxV2ReplyBtn" : ""} ${
                  openingMode === mode ? "loading" : ""
                }`}
                onClick={() => startReply(mode)}
              >
                <Icon />
                {openingMode === mode ? "Opening..." : label}
              </button>
            ))}
          </div>

          {conversation?.step && (
            <div className="inboxV2ThreadFooter">
              <span className="inboxV2Pill">{conversation.step}</span>
            </div>
          )}
        </div>

        {replyOpen && (editorLoading || !editorReady) && (
          // Immediate feedback while the reply/forward content fetches, so the user isn't
          // left guessing (and re-clicking).
          <div className="inboxV2EditorLoading">
            <span className="inboxV2Spinner" />
            <span>Opening editor...</span>
          </div>
        )}

        {replyOpen && editorReady && !editorLoading && (
          // Reuse the popup's class chain so all EmailComposeEditor styles apply; the
          // overlay/positioning of .emailPreviewContentPopup is neutralised in SCSS.
          <div className="emailPreviewContentPopup inboxV2InlineEditor">
            <div className="card">
              <div className="cardBody editorBody">
                <EmailComposeEditor
                  // EmailComposeEditor seeds its state once on mount; key by emailType so
                  // switching Reply -> Reply All -> Forward remounts it with the new content.
                  key={emailPreviewContentPopupState.editorInputValue?.emailType}
                  createTs={lastInbound?.date || headerMessage?.date || ""}
                  repliedFromAddress={lastInbound?.from || ""}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {showScrollBtn && (
        <button
          type="button"
          className="inboxV2LiScrollDown inboxV2ThreadScrollDown"
          onClick={jumpToLatest}
        >
          <i className="material-icons">keyboard_arrow_down</i>
          Latest messages
        </button>
      )}
    </div>
  );
});

export default EmailThread;
